import React, { useRef, useEffect } from "react";
import {
  NumberFormatValues,
  NumericFormat,
} from "react-number-format";
import {
  ICurrencyList,
  IIconStyles,
  ISalaryRangeOptions,
} from "../types/salaryRange";
import "./../css/autoComplete.css";
import DropdownIcon from "../icons/DropdownIcon";

interface AutocompleteProps {
  isIndianCurrency: boolean;
  options: ISalaryRangeOptions[];
  value: string;
  handleChange: (
    event: React.SyntheticEvent,
    newValue: ISalaryRangeOptions | null
  ) => void;
  handleInputChange: (value: string, type: "min" | "max", isUpdatedFromProps?:boolean) => void;
  handleBlur: (type: "min" | "max") => void;
  fieldName: string;
  selectedCurrency: ICurrencyList | null;
  searchText: string;
  customFilter: (
    options: ISalaryRangeOptions[],
    state: { inputValue: string }
  ) => ISalaryRangeOptions[];
  containerStyles?: React.CSSProperties;
  inputWrapperStyles?: React.CSSProperties;
  showDropdown: boolean;
  toggleDropdown: (value: "min" | "max" | null) => void;
  iconStyles?: IIconStyles;
}

const Autocomplete: React.FC<AutocompleteProps> = ({
  isIndianCurrency,
  options,
  value,
  handleChange,
  handleInputChange,
  fieldName,
  selectedCurrency,
  customFilter,
  containerStyles,
  inputWrapperStyles,
  showDropdown,
  toggleDropdown,
  handleBlur,
  iconStyles,
  searchText,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const maxValue = isIndianCurrency ? 10000000 : 9999999;

  const filteredOptions = customFilter(options, { inputValue: searchText });
  const isOpen = isIndianCurrency && showDropdown && !!filteredOptions?.length;

  const type = fieldName === "Min" ? "min" : "max";

  const handleValueChange = (values: NumberFormatValues, sourceInfo: any) => {
    const { value, formattedValue } = values;

    if(isIndianCurrency && formattedValue?.includes("+") && parseFloat(value) !== maxValue){
      handleInputChange(formattedValue?.replace(/[₹+]/g, "").trim(), type, sourceInfo?.source === "prop");
    }else{
      handleInputChange(formattedValue?.replace("₹", "")?.trim(), type, sourceInfo?.source === "prop");
    }
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        toggleDropdown(null);
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown, toggleDropdown]);

  return (
    <div
      className="salaryRange_autocompleteContainer"
      style={{ ...containerStyles }}
      ref={dropdownRef}
    >
      <div
        className="salaryRange_autoCompleteInputWrapper"
        style={{ ...inputWrapperStyles }}
      >
        <NumericFormat
          value={value}
          onValueChange={handleValueChange}
          thousandsGroupStyle={isIndianCurrency ? "lakh" : "thousand"}
          thousandSeparator=","
          prefix={isIndianCurrency ? "₹ " : ""}
          suffix={value.includes("+") && type ==="max" && isIndianCurrency ? "+" : ""}
          allowNegative={false}
          decimalScale={0}
          getInputRef={inputRef}
          style={{
            border: "none",
            outline: "none",
            fontSize: "14px",
            paddingLeft: "8px",
            width:"100%",
            height:"80%"
          }}
          onFocus={() => toggleDropdown(type)}
          onBlur={() => handleBlur(type)}
          placeholder={`${fieldName}`}
          isAllowed={(values) => {
            const { value } = values;

            if(!value){
              return true;
            }
            return +value <= maxValue;
          }}
        />
        {isIndianCurrency && (
          <span
            className={`salaryRange_dropdownIcon ${isOpen ? "rotate" : ""}`}
            style={{ marginRight: "8px" }}
            onClick={() => toggleDropdown(showDropdown ? null : type)}
          >
            <DropdownIcon
              fill={iconStyles?.fill}
              height={iconStyles?.height}
              stroke={iconStyles?.stroke}
              strokeWidth={iconStyles?.strokeWidth}
              width={iconStyles?.width}
            />
          </span>
        )}
      </div>
      {isOpen && (
        <ul className="salaryRange_autocompleteDropdown">
          {filteredOptions.map((option) => (
            <li
              key={option.value}
              className="salaryRange_autocompleteOption"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                handleChange(e, option);
              }}
            >
              {selectedCurrency?.sign || ""} {option.value}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Autocomplete;
